// packages/ui-kit/src/primitives/Banner.tsx
// C3F v18 — reference: design/system/components/feedback/Banner.{jsx,prompt.md}
//
// A notice ANCHORED to the content, not floating: it is born from a lasting
// state of the structure and stays as long as that state lasts. The Toast is
// its opposite — the consequence of a just-completed action, gone on its own.
// Putting a lasting condition in a toast makes it vanish before it is read.
import type { CSSProperties, ReactNode } from "react";

type Tone = "warning" | "danger" | "success" | "info" | "accent";
type StrongTone = "danger" | "success" | "accent";

interface Props {
  tone?: Tone;
  /**
   * Full state-colored fill instead of the soft tint: only for a state that
   * must not be able to pass unnoticed (impersonation, destructive mode).
   * No strong `warning`: yellow with light text never reaches 4.5:1 — it
   * renders as strong danger, as does `info`.
   */
  strong?: boolean;
  /** Sticks to the top of the scroll container. */
  sticky?: boolean;
  icon?: ReactNode;
  children?: ReactNode;
  /** Buttons on the right: `size="sm"`, usually `secondary` or `ghost` — no
   *  props needed for legibility, the surface carries its own ink. */
  actions?: ReactNode;
  /**
   * Omit when the state must not be hideable. The strong danger banner
   * ignores it by design: a dangerous state stays visible until it ends.
   */
  onDismiss?: () => void;
  className?: string;
}

// CSSProperties + custom properties: the banner redeclares tokens on its subtree.
type VarStyle = CSSProperties & Record<`--${string}`, string>;

const softBg: Record<Tone, string> = {
  warning: "bg-warning-soft",
  danger: "bg-danger-soft",
  success: "bg-success-soft",
  info: "bg-info-soft",
  accent: "bg-accent-soft",
};

// The state color is both the dot AND the secondary ink/outline of the soft
// branch. As TEXT, accent must be --accent-text, never --accent (the fill).
const softState: Record<Tone, string> = {
  warning: "--warning",
  danger: "--danger",
  success: "--success",
  info: "--info",
  accent: "--accent-text",
};

const softDot: Record<Tone, string> = {
  warning: "bg-warning",
  danger: "bg-danger",
  success: "bg-success",
  info: "bg-info",
  accent: "bg-accent-text",
};

/*
 * Soft branch: the theme's secondary inks give out on the tints (--fg-muted
 * 4.01:1, --fg-subtle 2.30:1 on warning-soft Night) and --border-control is
 * calibrated on the theme's four surfaces, not on a state tint. The state
 * color on its own tint measures 4.61–4.94:1 Night / 3.38–5.90:1 Paper, so it
 * serves as secondary ink AND outline. --fg-default (16.71/9.28), the box
 * borders and the alpha veils survive the tint — the theme keeps those.
 */
function softVars(tone: Tone): VarStyle {
  const state = `var(${softState[tone]})`;
  return {
    "--fg-muted": state,
    "--fg-subtle": state,
    "--sidebar-muted": state,
    "--accent-text": state,
    "--border-control": state,
  };
}

const strongBg: Record<StrongTone, string> = {
  danger: "bg-banner-danger",
  success: "bg-banner-success",
  accent: "bg-banner-accent",
};

const strongFill: Record<StrongTone, string> = {
  danger: "--banner-danger",
  success: "--banner-success",
  accent: "--banner-accent",
};

/*
 * Strong branch: fixed fills (the Night state tokens are light inks — used as
 * fills with white text they measure 2.2:1), so the surface is the same in
 * both themes and the theme governs nothing on it. It therefore redeclares the
 * ENTIRE ink family, paper-derived — not just the variables today's case
 * needs: every token left out stays anchored to the theme (that is how
 * --fg-muted once sat at 2.64:1 with --fg-default already fixed). On an alarm
 * surface no ink is weak: even --fg-subtle holds AA. Alphas measured on the
 * most demanding fill (#5B2FD6): .86→4.89 · .82→4.57 · .75→4.06.
 */
function strongVars(tone: StrongTone): VarStyle {
  const paper = (a: number) => `rgba(244, 240, 233, ${a})`;
  return {
    "--fg-default": "#FFFFFF",
    "--fg-strong": "#FFFFFF",
    "--fg-muted": paper(0.86),
    "--fg-subtle": paper(0.82),
    "--sidebar-muted": paper(0.86),
    "--fg-inverse": `var(${strongFill[tone]})`,
    "--accent-text": "#FFFFFF",
    "--border-control": paper(0.75),
    "--border-default": paper(0.42),
    "--border-subtle": paper(0.24),
    "--bg-hover": "rgba(255, 255, 255, 0.14)",
    "--bg-active": "rgba(255, 255, 255, 0.22)",
  };
}

export function Banner({
  tone = "warning",
  strong = false,
  sticky = false,
  icon,
  children,
  actions,
  onDismiss,
  className = "",
}: Props) {
  const strongTone: StrongTone = tone === "success" || tone === "accent" ? tone : "danger";
  // The strong danger banner cannot be dismissed: it flags a dangerous state
  // that must stay visible until the state itself ends.
  const dismissible = onDismiss !== undefined && !(strong && strongTone === "danger");
  return (
    <div
      role={tone === "danger" ? "alert" : "status"}
      style={strong ? strongVars(strongTone) : softVars(tone)}
      className={`flex min-h-touch items-center gap-3 px-4 py-2 text-fg-default ${
        strong ? strongBg[strongTone] : `${softBg[tone]} border-b border-border-default`
      } ${sticky ? "sticky top-0 z-50" : ""} ${className}`}
    >
      {!strong && (
        <span aria-hidden className={`h-[7px] w-[7px] shrink-0 rounded-full ${softDot[tone]}`} />
      )}
      {icon && (
        <span aria-hidden className="flex shrink-0">
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-1 text-sm">{children}</span>
      {actions && <span className="flex shrink-0 gap-2">{actions}</span>}
      {dismissible && (
        <button
          type="button"
          aria-label="Chiudi l'avviso"
          onClick={onDismiss}
          // Attenuated with a COLOR, never opacity: --fg-muted is already
          // redeclared by the banner itself, on both branches. The only way to
          // close the notice cannot be its least legible piece.
          className="tap-target grid h-[34px] w-[34px] shrink-0 place-items-center rounded-sm text-lede leading-none text-fg-muted transition-colors duration-fast hover:bg-hover"
        >
          {"×"}
        </button>
      )}
    </div>
  );
}
